import React from 'react'

import Container from 'components/atoms/Container'
import Row from 'components/atoms/Row'
import Col from 'components/atoms/Col'
import Avatar from 'components/atoms/Avatar'

import Typography from 'components/atoms/Typography'
import LikeButton from 'components/molecules/LikeButton'
import PlayButton from 'components/atoms/SVGIcons/PlayButton'

import intl from 'react-intl-universal'
import triggerEvent from 'utils/dataLayer'

import { string, node, bool, shape, func } from 'prop-types'

const SchoolPageBanner = ({
  title,
  slug,
  subtitle,
  logo,
  content,
  imgSrc,
  likeTarget,
  likeStatus,
  kind,
  mainVideo,
  onVideoClick,
}) => {
  const imageBg = (mainVideo && mainVideo.thumbnail) || imgSrc || ''

  return (
    <div className="school-page-banner">
      <Row className="full-height">
        <Col lg="8">
          <div className="school-page-banner__image" >
            <div className="school-page-banner__image-blur"/>
            <Container fluid>
              <div className="school-page-banner__like-btn">
                <LikeButton
                  active={likeStatus}
                  kind={kind}
                  targetId={likeTarget}
                  type="campus"
                  variant="solid"
                  forDetailPage
                  className="m-y-md m-x-sm"
                  complete
                  useNewIcon
                  handleClick={() => triggerEvent('campus_favourite', {
                    campus_name: title,
                    favorite_button_location: 'top',
                    page_location: `/directory/${slug}`,
                  })}
                >
                  <span className="hidden-sm-down">
                    {likeStatus ? intl.get('common.remove_from_favorite') : intl.get('common.add_to_favorite')}
                  </span>
                </LikeButton>
              </div>
            </Container>
            {
              (mainVideo && mainVideo.source) &&
              <div className="school-page-banner__play-btn" onClick={() => onVideoClick(mainVideo.source, mainVideo.title)}>
                <PlayButton width={86} height={86} />
              </div>
            }
          </div>
        </Col>
        <Col lg={{ order: 'first', size: 4 }}>
          <Container className="school-page-banner__info">
            {
              logo && (
                <Avatar
                  className="school-page-banner__logo"
                  size={80}
                >
                  <img src={logo} alt={`${title} logo`}/>
                </Avatar>
              )
            }
            <Typography className="school-page-banner__title" component="h1" variant="headline" fontWeight="bold">
              { title }
            </Typography>
            <Typography className="school-page-banner__subbody" component="p" variant="subbody">
              { subtitle }
            </Typography>
            {
              content && (
                <div className="school-page-banner__content">
                  { content }
                </div>
              )
            }
          </Container>
        </Col>
      </Row>

      <style jsx>
        {`
          .school-page-banner {
            background-color: #e3ecf5;
            @media screen and (min-width: 1024px) {
              height: 560px;
            }
            :global(.full-height, .col, &__info) {
              height: 100%;
            }
            :global(&__info) {
              display: flex;
              flex-direction: column;
              justify-content: center;
              @media screen and (min-width: 769px) {
                padding: ${
                  content ? '0 24px 0 80px' : '0 24px 0 80px'
                }!important;
              }
            }
            :global(&__title) {
              margin-top: 16px !important;
              line-height: 1.5;
              color: #0c2846;
              @media screen and (min-width: 768px) {
                font-size: 24px !important;
              }
            }
            :global(&__subbody) {
              @media screen and (min-width: 768px) {
                font-size: 16px !important;
              }
            }
            &__like-btn {
              display: flex;
              justify-content: flex-end;
            }
            &__image {
              background-image: url(${imageBg});
              background-size: cover;
              background-position: center;
              background-repeat: no-repeat;
              width: 100%;
              height: 100%;
              min-height: 320px;
              position: relative;
            }
            &__image-blur {
              background-image: linear-gradient(to left, rgba(255, 255, 255, 0), #e3ecf5);
              height: 100%;
              width: 144px;
              display: none;
              @media screen and (min-width: 992px) {
                display: block;
                position: absolute;
                top: 0;
                bottom: 0;
                left: 0;
              }
            }
            &__play-btn {
              position: absolute;
              top: 0;
              right: 0;
              bottom: 0;
              left: 0;
              width: 100%;
              height: 100%;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              :global(svg) {
                @media screen and (min-width: 768px) {
                  width: 150px !important;
                  height: 150px !important;
                }
              }
            }
            :global(&__logo) {
              @media screen and (min-width: 768px) {
                width: 128px !important;
                height: 128px !important;
              }
            }
          }
        `}
      </style>
    </div>
  )
}

SchoolPageBanner.propTypes = {
  title: string.isRequired,
  slug: string,
  subtitle: string,
  logo: string,
  content: node,
  imgSrc: string.isRequired,
  likeStatus: bool,
  likeTarget: string,
  kind: string,
  onVideoClick: func,
  mainVideo: shape({
    source: string,
    thumbnail: string,
  }),
}

export default SchoolPageBanner
